import { TagProps, TooltipProps } from '@chakra-ui/react';
import { assertNever } from '@varos/util-typescript';
import { capitalize } from 'lodash';
import { ReportingControllerBaseConfig } from '../../base';
import { ResourceBlockController } from './resourceBlockInterface';
import { ResourceBlockViewProps } from './resourceBlockProps';
import { ResourceBlockAggregate } from './resourceBlockModel';
import { createBlockItemController } from '../block';
import { FaEye, FaTrash } from 'react-icons/fa';
import { HiCog } from 'react-icons/hi';
import { AiOutlineEye } from 'react-icons/ai';

export function createResourceBlockController(
    config: ReportingControllerBaseConfig
): ResourceBlockController {
    const {
        kernel: {
            infra: { formatter },
        },
    } = config;
    const blockController = createBlockItemController();
    function getStatusProps(item: ResourceBlockAggregate): {
        isLoading: boolean;
        tag: Pick<TagProps, 'colorScheme'>;
        // tooltip: Pick<TooltipProps, 'label'>;
        label: string;
        description: string;
        hint: string | null;
    } {
        if (item.resource.status === 'creating' || item.resource.status === 'pending') {
            return {
                label: 'Creating',
                isLoading: true,
                tag: {
                    colorScheme: 'orange',
                },
                description: 'Please wait while the resource is being created',
                hint: null,
            };
        }
        if (item.resource.status === 'ready') {
            return {
                label: 'Active',
                isLoading: false,
                tag: {
                    colorScheme: 'green',
                },
                description: 'Resource has been created and is ready to use',
                hint: null,
            };
        }
        if (item.resource.status === 'unhealthy') {
            return {
                label: 'Unhealthy',
                isLoading: false,
                tag: {
                    colorScheme: 'red',
                },
                description: 'Resource is created but not healthy',
                hint: null,
            };
        }
        if (item.resource.status === 'error') {
            return {
                label: 'Failed',
                isLoading: false,
                tag: {
                    colorScheme: 'red',
                },
                description: item.resource.error?.description ?? '',
                hint: item.resource.error?.hint ?? null,
            };
        }
        assertNever(item.resource.status);
    }
    return {
        useProps(context, item, props): ResourceBlockViewProps {
            const block = blockController.useProps(context, item, props.item);
            const statusProps = getStatusProps(item);
            return {
                label: item.resource.physical_id ?? item.resource.id,
                caption: capitalize(item.resource.type),
                status: {
                    isLoading: statusProps.isLoading,
                    title: statusProps.label,
                    description: statusProps.description,
                    // hint: statusProps.hint,
                    tag: statusProps.tag,
                    timestamp: {
                        relative: {
                            label: formatter.timeago(item.resource.status_changed_at, {
                                suffix: true,
                                notation: 'short',
                            }),
                        },
                    },
                },
                inspect: {
                    modal: {
                        isOpen: props.modal.disclosure.isOpen,
                        onClose: props.modal.disclosure.onClose,
                    },
                },
                configuration: {
                    raw: JSON.stringify(item.resource.config, null, 2),
                },
                block: {
                    ...block,
                    menu: {
                        ...block.menu,
                        items: [
                            {
                                kind: 'item',
                                id: 'inspect',
                                description: 'Inspect',
                                colorScheme: null,
                                Icon: AiOutlineEye,
                                button: {
                                    onClick() {
                                        props.modal.disclosure.onOpen();
                                    },
                                },
                            },
                            { kind: 'divider' },
                            ...block.menu.items,
                        ],
                    },
                },
            };
        },
    };
}
